// Amazon "add to cart" link: one URL that adds several products with quantities to the
// signed-in user's Amazon.com cart. Documented as Amazon's Add to Cart form
// (https://webservices.amazon.com/paapi5/documentation/add-to-cart-form.html).
//
// UNVERIFIED (see docs/OPEN_QUESTIONS.md): whether it works without an AssociateTag,
// how many items one link holds, and whether same-day perishables land in the
// same-day cart. Test with a real account before relying on it.

export interface AmazonLine {
  asin: string;
  qty: number;
}

const ASIN_RE = /^[A-Z0-9]{10}$/;

export function isValidAsin(asin: string): boolean {
  return ASIN_RE.test(asin.trim().toUpperCase());
}

/** Pull an ASIN out of a pasted Amazon product URL or return the input if it already is one. */
export function extractAsin(input: string): string | undefined {
  const s = input.trim();
  if (isValidAsin(s)) return s.toUpperCase();
  const m = s.match(/(?:\/dp\/|\/gp\/product\/|\/gp\/aw\/d\/|[?&]asin=)([A-Z0-9]{10})/i);
  return m ? m[1].toUpperCase() : undefined;
}

/** Amazon's id for the Amazon Fresh "brand" (base64 of "Amazon Fresh"), used to target the Fresh cart. */
export const AMAZON_FRESH_BRAND_ID = 'QW1hem9uIEZyZXNo';

/**
 * `fresh` adds the Amazon Fresh brand id so items land in the Fresh cart instead of the
 * regular one. UNVERIFIED like the rest of this link: test on a real account.
 */
export function buildAmazonCartUrl(lines: AmazonLine[], associateTag?: string, opts: { fresh?: boolean } = {}): string {
  const valid = lines.filter((l) => isValidAsin(l.asin) && l.qty > 0);
  if (valid.length === 0) throw new Error('No valid Amazon products to add.');
  const params = new URLSearchParams();
  if (associateTag) params.set('AssociateTag', associateTag);
  if (opts.fresh) params.set('almBrandId', AMAZON_FRESH_BRAND_ID);
  valid.forEach((l, i) => {
    params.set(`ASIN.${i + 1}`, l.asin.toUpperCase());
    params.set(`Quantity.${i + 1}`, String(Math.max(1, Math.round(l.qty))));
  });
  return `https://www.amazon.com/gp/aws/cart/add.html?${params.toString()}`;
}

/** Search link used for items with no matched product yet (`fresh` searches Amazon Fresh only). */
export function amazonSearchUrl(query: string, opts: { fresh?: boolean } = {}): string {
  return `https://www.amazon.com/s?k=${encodeURIComponent(query)}${opts.fresh ? '&i=amazonfresh' : ''}`;
}
